// src/host/HostCalendar.js
// You’re Welcome — Host Calendar (polished v4.0, Airbnb-style minimal)
// - Keeps ALL original data flow + Firestore interactions
// - Fixes: proxy-only iCal fetch, auto-preview on add, refresh button
// - UI: clean right-rail cards, light accordions, no clutter
// - Mobile: sticky calendar header + bottom-sheet sharing same rail content
// FIX in this version: RailContent/Section hoisted out of HostCalendar to prevent remount.
// That removes the “one letter at a time / cursor reset” glitch.
// NO logic changes.
//
// EDIT (sync fix): add live listener to listings/{listingId}/calendar for visible range,
// so external busy days show instantly without refresh.
//
// FIX (bug): FieldPath.documentId() is not valid in v9 modular SDK.
// Use documentId() instead.
// EDIT (UX): auto-preview existing feeds once on mount so busy overlays appear
// without requiring the Refresh button.
// EDIT (PERSISTENCE): Add importNow call on Add/Refresh + one-time auto-import
// for feeds that haven’t synced yet. This ensures busy days persist across reloads.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/useAuth";
import {
  getFirestore,
  collection,
  onSnapshot,
  query,
  where,
  doc,
  getDoc,
  addDoc,
  deleteDoc,
  updateDoc,
  serverTimestamp,
  // range-query calendar docs by docId
  orderBy,
  startAt,
  endAt,
  documentId, // ✅ FIXED import
} from "firebase/firestore";
import {
  createBlock,
  unblockDate,
  watchBookingsRangeAll,
  ymd,
  startOfMonth,
  endOfMonth,
  addMonths,
} from "../firebase";

/** ============================== Config ============================== **/
const BRAND = "#7C9082";
const BRAND_DARK = "#66796E";

const APP_DOMAIN = "www.yourewelcomeapp.com";
// Outbound (read-only) per-listing feed:
const ICS_FEED_BASE = `https://${APP_DOMAIN}/ical`;
// Backend proxy for third-party iCal (must be server-side):
const ICS_PROXY_BASE = `https://${APP_DOMAIN}/api/ics`;

const ROUTES = { newListing: "/host/listings/new" };

/** ============================== Utils =============================== **/
const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
const isSameDay = (a, b) => ymd(a) === ymd(b);

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const startOfToday = () => startOfDay(new Date());

const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

const parseYYYYMMDD = (s) => {
  const [Y, M, D] = String(s || "").split("-").map(Number);
  if (!Y || !M || !D) return null;
  return new Date(Y, M - 1, D);
};

const randGroupId = () =>
  `grp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

/** 6x7 calendar grid for visible month */
function buildMonthGrid(anchor) {
  const first = startOfMonth(anchor);
  const last = endOfMonth(anchor);
  const lead = first.getDay();
  const totalCells = 42;
  const grid = [];
  const start = new Date(first);
  start.setDate(first.getDate() - lead);
  for (let i = 0; i < totalCells; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    grid.push(d);
  }
  return { grid, first, last };
}

/** Span collector by groupId in a date→info map */
function collectGroupDatesFromMap(map, dateStr, limit = 60) {
  const info = map.get(dateStr);
  if (!info?.groupId) return [dateStr];
  const gid = info.groupId;
  const out = [dateStr];
  const base = parseYYYYMMDD(dateStr);
  for (let i = 1; i < limit; i++) {
    const ds = ymd(addDays(base, -i));
    const x = map.get(ds);
    if (x?.isActive && x.groupId === gid) out.unshift(ds);
    else break;
  }
  for (let i = 1; i < limit; i++) {
    const ds = ymd(addDays(base, +i));
    const x = map.get(ds);
    if (x?.isActive && x.groupId === gid) out.push(ds);
    else break;
  }
  return out;
}

/** Friendly source detection for badges */
const detectSource = (url = "") => {
  const u = url.toLowerCase();
  if (u.includes("airbnb.com")) return "airbnb";
  if (u.includes("vrbo.com") || u.includes("homeaway.com")) return "vrbo";
  if (u.includes("google.com")) return "google";
  if (u.includes("calendar.apple.com") || u.includes("icloud.com"))
    return "apple";
  return "other";
};

/** Colors for per-feed chips/overlays */
const FEED_COLORS = [
  "#E57373",
  "#64B5F6",
  "#81C784",
  "#FFB74D",
  "#BA68C8",
  "#4DB6AC",
  "#A1887F",
  "#90A4AE",
];
const colorForIndex = (i) => FEED_COLORS[i % FEED_COLORS.length];

/** Minimal iCal parser (all-day DTSTART/DTEND spans) */
function parseICS(text) {
  try {
    const events = [];
    const regex = /BEGIN:VEVENT([\s\S]*?)END:VEVENT/gm;
    let m;
    while ((m = regex.exec(text))) {
      const body = m[1];
      const pick = (k) => {
        const r = new RegExp(`^${k}(:|;[^\\n]*:)\\s*(.+)$`, "m");
        const mm = body.match(r);
        return mm ? mm[2].trim() : "";
      };
      const rawStart = pick("DTSTART");
      const rawEnd = pick("DTEND");
      const summary = pick("SUMMARY");
      if (!rawStart) continue;

      const toDate = (s) => {
        if (!s) return null;
        const d = s.replace(/[^0-9]/g, "");
        if (d.length === 8) {
          const Y = +d.slice(0, 4),
            M = +d.slice(4, 6) - 1,
            D = +d.slice(6, 8);
          return new Date(Y, M, D);
        }
        if (d.length >= 15) {
          const Y = +d.slice(0, 4),
            M = +d.slice(4, 6) - 1,
            D = +d.slice(6, 8),
            h = +d.slice(9, 11) || 0,
            mi = +d.slice(11, 13) || 0,
            s_ = +d.slice(13, 15) || 0;
          return new Date(Date.UTC(Y, M, D, h, mi, s_));
        }
        return null;
      };

      const sDate = toDate(rawStart);
      const eDate = toDate(rawEnd);
      if (!sDate) continue;

      const endInclusive = eDate ? addDays(eDate, -1) : sDate; // DTEND exclusive
      sDate.setHours(0, 0, 0, 0);
      endInclusive.setHours(0, 0, 0, 0);

      const days = [];
      let cur = new Date(sDate);
      while (cur <= endInclusive) {
        days.push(ymd(cur));
        cur = addDays(cur, 1);
      }

      events.push({
        summary: summary || "Busy",
        start: sDate,
        end: endInclusive,
        days,
      });
    }
    return events;
  } catch {
    return [];
  }
}

/** Fetch ICS through proxy ONLY (Airbnb links require server-side fetch) */
async function fetchICS(url) {
  const res = await fetch(
    `${ICS_PROXY_BASE}/proxy?url=${encodeURIComponent(url)}`
  );
  if (!res.ok) throw new Error("Proxy could not download that calendar.");
  const text = await res.text();
  if (!/BEGIN:VCALENDAR/.test(text))
    throw new Error("That link didn’t return a valid .ics calendar.");
  return text;
}

/** ---------------------- Hoisted UI helpers (stable identity) -------- **/

function Section({ title, open, onToggle, children }) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-4 py-3 text-left"
        aria-expanded={open}
      >
        <div className="text-sm font-semibold">{title}</div>
        <div className="text-neutral-400">{open ? "–" : "+"}</div>
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

function RailContent({
  compact = false,

  // selection / maps
  selectedDate,
  selectedDateStr,
  baseMap,
  overlayMap,
  monthMap,
  grid,
  monthAnchor,

  // listing / rules
  selectedListingId,
  selectedListingRules,
  DEFAULT_RULES,
  rulesDirty,
  setSelectedListingRules,
  setRulesDirty,

  // range/single UI
  rangeMode,
  setRangeMode,
  rangeStart,
  setRangeStart,
  rangeEnd,
  setRangeEnd,
  note,
  setNote,
  saving,

  // actions
  handleBlock,
  handleUnblock,
  handleUnblockSpan,
  handleBlockRange,
  handleSaveRules,
  collectGroupDates,

  // feeds
  feeds,
  previewEvents,
  newFeed,
  setNewFeed,
  addingFeed,
  addFeed,
  refreshAllFeeds,
  testAndPreview,
  toggleAutoImport,
  removeFeed,
  applyExternalAsBlocks,
  testingFeedId,

  // subscribe/export
  feedUrlHttps,
  feedUrlWebcal,
  copyText,
  copiedHttps,
  copiedWebcal,
  exportVisibleMonthICS,
}) {
  const [openAvailability, setOpenAvailability] = useState(true);
  const [openRules, setOpenRules] = useState(false);
  const [openSync, setOpenSync] = useState(true);

  const info = selectedDateStr ? baseMap.get(selectedDateStr) : null;
  const overlay =
    selectedDateStr &&
    (overlayMap.get(selectedDateStr) || monthMap.get(selectedDateStr));

  const overlayText =
    overlay?.holdType === "prep"
      ? "Prep hold (turnover buffer)"
      : overlay?.holdType === "cutoff"
      ? "Advance notice cutoff"
      : overlay?.holdType === "window"
      ? "Outside booking window"
      : overlay?.holdType === "weekday"
      ? "Closed by weekday rule"
      : overlay?.status === "external"
      ? `Busy from ${overlay.source || "external"}`
      : null;

  const canBlockSelected =
    selectedDate &&
    startOfDay(selectedDate) >= startOfToday() &&
    !(info?.isActive) &&
    overlay?.status !== "hold";

  const groupDates =
    info?.groupId && selectedDateStr ? collectGroupDates(selectedDateStr) : [];

  return (
    <div className={`space-y-3 ${compact ? "" : "sticky top-4"}`}>
      {/* Availability / Selected day */}
      <Section
        title="Availability"
        open={openAvailability}
        onToggle={() => setOpenAvailability((v) => !v)}
      >
        <div className="text-xs text-neutral-500">
          {selectedDate
            ? new Date(selectedDate).toLocaleDateString()
            : "Select a day on the calendar"}
        </div>

        <div className="mt-3 inline-flex rounded-full border border-neutral-300 p-1 text-sm">
          <button
            className={`rounded-full px-3 py-1.5 ${
              !rangeMode ? "text-white" : "hover:bg-neutral-100"
            }`}
            onClick={() => setRangeMode(false)}
            aria-pressed={!rangeMode}
            style={{ backgroundColor: !rangeMode ? BRAND : "transparent" }}
          >
            Single
          </button>
          <button
            className={`rounded-full px-3 py-1.5 ${
              rangeMode ? "text-white" : "hover:bg-neutral-100"
            }`}
            onClick={() => setRangeMode(true)}
            aria-pressed={rangeMode}
            style={{ backgroundColor: rangeMode ? BRAND : "transparent" }}
          >
            Range
          </button>
        </div>

        <div className="mt-3">
          <label className="text-xs text-neutral-600">Note (optional)</label>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="mt-1 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
            placeholder="Host blocked"
          />
        </div>

        {!rangeMode && selectedDate && (
          <div className="mt-3 rounded-xl border border-neutral-200 bg-neutral-50 p-3 text-sm">
            {info?.isActive ? (
              <>
                <div className="font-medium">
                  {info.status === "pending"
                    ? "Pending booking"
                    : info.status === "blocked"
                    ? "Blocked"
                    : "Booked"}
                </div>
                <div className="mt-1 text-neutral-600">
                  This date is unavailable.
                </div>
                <div className="mt-2 text-xs text-neutral-500">
                  Note: {info.note || "—"}
                </div>

                {info.status === "blocked" && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      onClick={() => handleUnblock(selectedDate)}
                      disabled={saving}
                      className="rounded-full border border-neutral-300 px-4 py-1.5 text-sm hover:bg-white disabled:opacity-60"
                    >
                      {saving ? "Unblocking…" : "Unblock"}
                    </button>
                    {info.groupId && groupDates.length > 1 && (
                      <button
                        onClick={() => handleUnblockSpan(selectedDate)}
                        disabled={saving}
                        className="rounded-full border border-neutral-300 px-4 py-1.5 text-sm hover:bg-white disabled:opacity-60"
                      >
                        {saving
                          ? "Unblocking…"
                          : `Unblock span (${groupDates.length})`}
                      </button>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="text-neutral-700">
                  {overlayText ? `Closed: ${overlayText}` : "Available"}
                </div>
                <button
                  onClick={() => handleBlock(selectedDate)}
                  disabled={!canBlockSelected || saving || !selectedListingId}
                  className="mt-3 w-full rounded-full px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                  style={{ backgroundColor: BRAND }}
                >
                  {saving ? "Blocking…" : "Block this date"}
                </button>
                <div className="mt-2 text-xs text-neutral-500">
                  Tip: Long-press a day to block/unblock quickly.
                </div>
              </>
            )}
          </div>
        )}

        {rangeMode && (
          <div className="mt-3 rounded-xl border border-neutral-200 p-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="text-sm">
                <span className="font-medium">Start</span>
                <input
                  type="date"
                  min={ymd(startOfToday())}
                  value={rangeStart}
                  onChange={(e) => {
                    setRangeStart(e.target.value);
                    const s = parseYYYYMMDD(e.target.value);
                    const eDate = parseYYYYMMDD(rangeEnd);
                    if (s && eDate && eDate < s) setRangeEnd(e.target.value);
                  }}
                  className="mt-1 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
                />
              </label>

              <label className="text-sm">
                <span className="font-medium">End</span>
                <input
                  type="date"
                  min={rangeStart || ymd(startOfToday())}
                  value={rangeEnd}
                  onChange={(e) => setRangeEnd(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
                />
              </label>
            </div>

            <div className="mt-3 flex gap-2">
              <button
                onClick={handleBlockRange}
                disabled={!rangeStart || !rangeEnd || saving || !selectedListingId}
                className="flex-1 rounded-full px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                style={{ backgroundColor: BRAND }}
              >
                {saving ? "Blocking…" : "Block range"}
              </button>
              <button
                onClick={() => {
                  setRangeStart("");
                  setRangeEnd("");
                }}
                disabled={saving}
                className="rounded-full border border-neutral-300 px-4 py-2 text-sm hover:bg-neutral-50 disabled:opacity-60"
              >
                Clear
              </button>
            </div>

            <p className="mt-2 text-xs text-neutral-500">
              Range blocks create a connected span for overnight stays.
            </p>
          </div>
        )}
      </Section>

      {/* Rules */}
      <Section
        title="Availability rules"
        open={openRules}
        onToggle={() => setOpenRules((v) => !v)}
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="text-sm">
            <span className="font-medium">Booking window (days)</span>
            <input
              type="number"
              min={0}
              max={1825}
              value={
                selectedListingRules?.bookingWindowDays ??
                DEFAULT_RULES.bookingWindowDays
              }
              onChange={(e) => {
                setSelectedListingRules((r) => ({
                  ...(r || DEFAULT_RULES),
                  bookingWindowDays: clamp(
                    Number(e.target.value) || 0,
                    0,
                    1825
                  ),
                }));
                setRulesDirty(true);
              }}
              className="mt-1 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
            />
          </label>

          <label className="text-sm">
            <span className="font-medium">Advance notice (hours)</span>
            <input
              type="number"
              min={0}
              max={72}
              value={
                selectedListingRules?.advanceNoticeHours ??
                DEFAULT_RULES.advanceNoticeHours
              }
              onChange={(e) => {
                setSelectedListingRules((r) => ({
                  ...(r || DEFAULT_RULES),
                  advanceNoticeHours: clamp(
                    Number(e.target.value) || 0,
                    0,
                    72
                  ),
                }));
                setRulesDirty(true);
              }}
              className="mt-1 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
            />
          </label>
        </div>

        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="text-sm">
            <span className="font-medium">Min nights</span>
            <input
              type="number"
              min={1}
              max={90}
              value={selectedListingRules?.minNights ?? DEFAULT_RULES.minNights}
              onChange={(e) => {
                const v = clamp(Number(e.target.value) || 1, 1, 90);
                setSelectedListingRules((r) => ({
                  ...(r || DEFAULT_RULES),
                  minNights: v,
                  maxNights: Math.max(
                    v,
                    r?.maxNights ?? DEFAULT_RULES.maxNights
                  ),
                }));
                setRulesDirty(true);
              }}
              className="mt-1 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
            />
          </label>

          <label className="text-sm">
            <span className="font-medium">Max nights</span>
            <input
              type="number"
              min={1}
              max={180}
              value={selectedListingRules?.maxNights ?? DEFAULT_RULES.maxNights}
              onChange={(e) => {
                const v = clamp(Number(e.target.value) || 30, 1, 180);
                setSelectedListingRules((r) => ({
                  ...(r || DEFAULT_RULES),
                  maxNights: v,
                }));
                setRulesDirty(true);
              }}
              className="mt-1 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
            />
          </label>
        </div>

        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="text-sm">
            <span className="font-medium">Prep days before</span>
            <input
              type="number"
              min={0}
              max={3}
              value={
                selectedListingRules?.prepDaysBefore ??
                DEFAULT_RULES.prepDaysBefore
              }
              onChange={(e) => {
                setSelectedListingRules((r) => ({
                  ...(r || DEFAULT_RULES),
                  prepDaysBefore: clamp(Number(e.target.value) || 0, 0, 3),
                }));
                setRulesDirty(true);
              }}
              className="mt-1 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
            />
          </label>

          <label className="text-sm">
            <span className="font-medium">Prep days after</span>
            <input
              type="number"
              min={0}
              max={3}
              value={
                selectedListingRules?.prepDaysAfter ??
                DEFAULT_RULES.prepDaysAfter
              }
              onChange={(e) => {
                setSelectedListingRules((r) => ({
                  ...(r || DEFAULT_RULES),
                  prepDaysAfter: clamp(Number(e.target.value) || 0, 0, 3),
                }));
                setRulesDirty(true);
              }}
              className="mt-1 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
            />
          </label>
        </div>

        <div className="mt-3">
          <div className="text-sm font-medium">Allowed weekdays (day-use)</div>
          <div className="mt-2 grid grid-cols-7 gap-2 text-sm">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
              (label, idx) => {
                const enabled =
                  selectedListingRules?.allowedWeekdays?.includes(idx);
                return (
                  <button
                    key={label}
                    type="button"
                    className={`rounded-lg border px-2 py-1 ${
                      enabled ? "text-white" : "text-neutral-700"
                    }`}
                    style={{
                      backgroundColor: enabled ? BRAND : "transparent",
                      borderColor: enabled ? BRAND : "#d4d4d8",
                    }}
                    onClick={() => {
                      setSelectedListingRules((r) => {
                        const set = new Set(
                          r?.allowedWeekdays ?? DEFAULT_RULES.allowedWeekdays
                        );
                        if (set.has(idx)) set.delete(idx);
                        else set.add(idx);
                        setRulesDirty(true);
                        return {
                          ...(r || DEFAULT_RULES),
                          allowedWeekdays: Array.from(set).sort((a, b) => a - b),
                        };
                      });
                    }}
                  >
                    {label}
                  </button>
                );
              }
            )}
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <button
            onClick={handleSaveRules}
            disabled={!rulesDirty || saving || !selectedListingId}
            className="rounded-full px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
            style={{ backgroundColor: BRAND }}
          >
            {saving ? "Saving…" : "Save rules"}
          </button>
          {!rulesDirty && (
            <span className="text-xs text-neutral-500">All changes saved</span>
          )}
        </div>
      </Section>

      {/* Sync Center */}
      <Section
        title="Sync calendars"
        open={openSync}
        onToggle={() => setOpenSync((v) => !v)}
      >
        <p className="text-xs text-neutral-500">
          Paste Airbnb/VRBO/Google iCal links. Busy days will preview instantly.
        </p>

        {/* Add feed */}
        <div className="mt-3 grid grid-cols-1 gap-2">
          <input
            placeholder="Label (Airbnb, VRBO, Google…) "
            value={newFeed.label}
            onChange={(e) =>
              setNewFeed((x) => ({ ...x, label: e.target.value }))
            }
            className="rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
          />
          <input
            placeholder="https://… (iCal URL)"
            value={newFeed.url}
            onChange={(e) =>
              setNewFeed((x) => ({ ...x, url: e.target.value }))
            }
            className="rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
          />
          <label className="flex items-center gap-2 text-xs text-neutral-600">
            <input
              type="checkbox"
              checked={newFeed.autoImport}
              onChange={(e) =>
                setNewFeed((x) => ({ ...x, autoImport: e.target.checked }))
              }
            />
            Auto-import on
          </label>

          <div className="flex gap-2">
            <button
              onClick={addFeed}
              disabled={addingFeed || !selectedListingId}
              className="flex-1 rounded-full px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              style={{ backgroundColor: BRAND }}
            >
              {addingFeed ? "Adding…" : "Add calendar"}
            </button>
            <button
              onClick={refreshAllFeeds}
              disabled={saving || feeds.length === 0}
              className="rounded-full border border-neutral-300 px-4 py-2 text-sm hover:bg-neutral-50 disabled:opacity-60"
            >
              {saving ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </div>

        {/* Existing feeds */}
        <div className="mt-4 space-y-2">
          {feeds.length === 0 && (
            <div className="rounded-xl border border-dashed border-neutral-300 p-3 text-xs text-neutral-500">
              No external calendars yet.
            </div>
          )}

          {feeds.map((f) => (
            <div key={f.id} className="rounded-xl border border-neutral-200 p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-block h-2.5 w-2.5 rounded-full"
                      style={{ backgroundColor: f.color || BRAND_DARK }}
                    />
                    <div className="truncate text-sm font-medium">
                      {f.label || "External calendar"}
                    </div>
                    {f.status === "ok" && (
                      <span className="rounded-full bg-green-100 px-2 py-0.5 text-[10px] text-green-800">
                        OK
                      </span>
                    )}
                    {f.status === "pending" && (
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] text-amber-800">
                        Pending
                      </span>
                    )}
                    {f.status === "error" && (
                      <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] text-red-800">
                        Error
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 truncate text-xs text-neutral-500">
                    {f.url}
                  </div>
                </div>

                <div className="shrink-0 flex items-center gap-1">
                  <button
                    onClick={() => testAndPreview(f)}
                    className="rounded-full border border-neutral-300 px-3 py-1.5 text-xs hover:bg-neutral-50"
                  >
                    {testingFeedId === f.id ? "Testing…" : "Preview"}
                  </button>
                  <button
                    onClick={() => toggleAutoImport(f)}
                    className="rounded-full border border-neutral-300 px-3 py-1.5 text-xs hover:bg-neutral-50"
                  >
                    {f.autoImport ? "Auto: On" : "Auto: Off"}
                  </button>
                  <button
                    onClick={() => removeFeed(f.id)}
                    className="rounded-full border border-neutral-300 px-3 py-1.5 text-xs hover:bg-neutral-50"
                  >
                    Remove
                  </button>
                </div>
              </div>

              {previewEvents[f.id]?.length > 0 && (
                <div className="mt-2 text-[11px] text-neutral-600">
                  Preview: {previewEvents[f.id].length} nearby busy events.
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Apply external busy */}
        <div className="mt-4 rounded-xl border border-neutral-200 p-3">
          <button
            onClick={applyExternalAsBlocks}
            className="w-full rounded-full px-4 py-2 text-sm font-semibold text-white"
            style={{ backgroundColor: BRAND }}
          >
            Apply external busy days (in view)
          </button>
          <p className="mt-2 text-xs text-neutral-500">
            This snapshots visible busy days into host-blocks. Keep Auto-import
            on for ongoing sync.
          </p>
        </div>

        {/* Subscribe links */}
        <div className="mt-3 rounded-xl border border-neutral-200 p-3">
          <div className="text-sm font-semibold">
            Your You’re Welcome calendar
          </div>
          <p className="mt-1 text-xs text-neutral-500">
            Paste into Google/Apple/Outlook to subscribe.
          </p>

          <div className="mt-3 space-y-3">
            <div>
              <label className="text-xs text-neutral-600">HTTPS URL</label>
              <div className="mt-1 flex items-center gap-2">
                <input
                  readOnly
                  value={feedUrlHttps}
                  className="w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none"
                />
                <button
                  type="button"
                  onClick={() => copyText(feedUrlHttps, "https")}
                  className="rounded-full border border-neutral-300 px-3 py-1.5 text-sm hover:bg-neutral-50"
                  disabled={!selectedListingId}
                >
                  {copiedHttps ? "Copied!" : "Copy"}
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs text-neutral-600">WebCal URL</label>
              <div className="mt-1 flex items-center gap-2">
                <input
                  readOnly
                  value={feedUrlWebcal}
                  className="w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none"
                />
                <button
                  type="button"
                  onClick={() => copyText(feedUrlWebcal, "webcal")}
                  className="rounded-full border border-neutral-300 px-3 py-1.5 text-sm hover:bg-neutral-50"
                  disabled={!selectedListingId}
                >
                  {copiedWebcal ? "Copied!" : "Copy"}
                </button>
                <a
                  href={feedUrlWebcal || undefined}
                  className="rounded-full px-3 py-1.5 text-sm font-semibold text-white"
                  style={{
                    backgroundColor: BRAND,
                    pointerEvents: feedUrlWebcal ? "auto" : "none",
                    opacity: feedUrlWebcal ? 1 : 0.6,
                  }}
                >
                  Open
                </a>
              </div>
            </div>

            <button
              type="button"
              onClick={exportVisibleMonthICS}
              className="w-full rounded-full border border-neutral-300 px-4 py-2 text-sm hover:bg-neutral-50"
              disabled={!selectedListingId}
            >
              Download visible month .ics snapshot
            </button>
          </div>
        </div>
      </Section>
    </div>
  );
}

/** ============================== Component =========================== **/
export default function HostCalendar() {
  const { user, loading } = useAuth();
  const db = getFirestore();

  // Listings & selection
  const [listings, setListings] = useState([]);
  const [selectedListingId, setSelectedListingId] = useState("");

  // Rules
  const DEFAULT_RULES = {
    bookingWindowDays: 365,
    advanceNoticeHours: 0,
    minNights: 1,
    maxNights: 30,
    prepDaysBefore: 0,
    prepDaysAfter: 0,
    dayUseStart: "09:00",
    dayUseEnd: "17:00",
    prepHoursBeforeDayuse: 0,
    prepHoursAfterDayuse: 0,
    allowedWeekdays: [0, 1, 2, 3, 4, 5, 6],
  };
  const [selectedListingRules, setSelectedListingRules] = useState(null);
  const [rulesDirty, setRulesDirty] = useState(false);

  // Month & bookings window
  const [monthAnchor, setMonthAnchor] = useState(() =>
    startOfMonth(new Date())
  );
  const [bookings, setBookings] = useState([]);

  // External feeds (subcollection)
  const [feeds, setFeeds] = useState([]);
  const [previewEvents, setPreviewEvents] = useState({});

  // Live imported external busy days
  const [calendarBlocks, setCalendarBlocks] = useState([]);

  // UI state
  const [selectedDate, setSelectedDate] = useState(null);
  const [rangeMode, setRangeMode] = useState(false);
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [note, setNote] = useState("Host blocked");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  // Copy feedback
  const [copiedHttps, setCopiedHttps] = useState(false);
  const [copiedWebcal, setCopiedWebcal] = useState(false);

  // New feed form
  const [newFeed, setNewFeed] = useState({
    label: "",
    url: "",
    autoImport: true,
  });
  const [addingFeed, setAddingFeed] = useState(false);
  const [testingFeedId, setTestingFeedId] = useState("");

  // Mobile sheet
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Auto-preview guard per listing
  const didPreviewRef = useRef(new Set());
  // ✅ PERSISTENCE: auto-import guard per listing
  const didImportRef = useRef(new Set());

  // Optional PWA CTA
  const [installPromptEvt, setInstallPromptEvt] = useState(null);
  const [showInstallCTA, setShowInstallCTA] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPromptEvt(e);
      setShowInstallCTA(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);
  const handleInstallClick = async () => {
    if (!installPromptEvt) return;
    installPromptEvt.prompt();
    try {
      await installPromptEvt.userChoice;
    } catch {}
    setShowInstallCTA(false);
    setInstallPromptEvt(null);
  };

  /** -------------------- Load host listings (live) -------------------- **/
  useEffect(() => {
    if (loading || !user) return;
    const q_ = query(
      collection(db, "listings"),
      where("ownerUid", "==", user.uid)
    );
    const unsub = onSnapshot(
      q_,
      (snap) => {
        const items = snap.docs.map((d) => {
          const x = d.data() || {};
          return {
            id: d.id,
            title: x.title || "Untitled",
            city: x.city || "",
            stateCode: x.stateCode || x.state || "",
          };
        });
        setListings(items);
        if (!selectedListingId && items.length)
          setSelectedListingId(items[0].id);
      },
      (err) => {
        console.error(err);
        setMsg("Couldn't load your listings.");
      }
    );
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user, db]);

  /** --------- Load booking rules for selected listing (once) ---------- **/
  useEffect(() => {
    let live = true;
    async function run() {
      if (!selectedListingId) {
        setSelectedListingRules(null);
        return;
      }
      try {
        const ref = doc(db, "listings", selectedListingId);
        const snap = await getDoc(ref);
        const data = snap.exists() ? snap.data() : {};
        const br = data.bookingRules || {};

const rules = {
  ...DEFAULT_RULES,
  ...br,

  // ✅ fallback to legacy top-level fields if bookingRules missing them
  minNights:
    br.minNights ??
    (data.minNights === "" ? null : data.minNights) ??
    DEFAULT_RULES.minNights,

  maxNights:
    br.maxNights ??
    (data.maxNights === "" ? null : data.maxNights) ??
    DEFAULT_RULES.maxNights,

  // ✅ NEW: legacy fallback for prep buffers
  prepDaysBefore:
    br.prepDaysBefore ??
    (data.prepDaysBefore === "" ? null : data.prepDaysBefore) ??
    DEFAULT_RULES.prepDaysBefore,

  prepDaysAfter:
    br.prepDaysAfter ??
    (data.prepDaysAfter === "" ? null : data.prepDaysAfter) ??
    DEFAULT_RULES.prepDaysAfter,
};

        rules.bookingWindowDays = clamp(
          Number(rules.bookingWindowDays) || 365,
          0,
          1825
        );
        rules.advanceNoticeHours = clamp(
          Number(rules.advanceNoticeHours) || 0,
          0,
          72
        );
        rules.minNights = clamp(Number(rules.minNights) || 1, 1, 90);
        rules.maxNights = clamp(
          Number(rules.maxNights) || 30,
          rules.minNights,
          180
        );
        rules.prepDaysBefore = clamp(Number(rules.prepDaysBefore) || 0, 0, 3);
        rules.prepDaysAfter = clamp(Number(rules.prepDaysAfter) || 0, 0, 3);
        rules.prepHoursBeforeDayuse = clamp(
          Number(rules.prepHoursBeforeDayuse) || 0,
          0,
          12
        );
        rules.prepHoursAfterDayuse = clamp(
          Number(rules.prepHoursAfterDayuse) || 0,
          0,
          12
        );
        rules.allowedWeekdays = Array.isArray(rules.allowedWeekdays)
          ? rules.allowedWeekdays
          : DEFAULT_RULES.allowedWeekdays;

        if (live) {
          setSelectedListingRules(rules);
          setRulesDirty(false);
        }
      } catch (e) {
        console.error(e);
        if (live) setSelectedListingRules({ ...DEFAULT_RULES });
      }
    }
    run();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedListingId]);

  /** --------------- Load bookings for selected listing (range) -------- **/
  useEffect(() => {
    if (!selectedListingId) {
      setBookings([]);
      return;
    }
    const { grid: windowGrid } = buildMonthGrid(monthAnchor);
    const from = ymd(windowGrid[0]);
    const to = ymd(windowGrid[windowGrid.length - 1]);
    const unsub = watchBookingsRangeAll({
      listingId: selectedListingId,
      fromYYYYMMDD: from,
      toYYYYMMDD: to,
      onChange: (rows) => setBookings(rows || []),
      onError: (err) => {
        console.error(err);
        setMsg("Couldn't load bookings for this listing.");
      },
    });
    return () => unsub && unsub();
  }, [selectedListingId, monthAnchor]);

  /** ---------------- Watch external feeds (subcollection) ------------- **/
  useEffect(() => {
    if (!selectedListingId) {
      setFeeds([]);
      return;
    }
    const feedsRef = collection(
      db,
      "listings",
      selectedListingId,
      "calendarFeeds"
    );
    const unsub = onSnapshot(
      feedsRef,
      (snap) => {
        const rows = snap.docs.map((d, i) => ({
          id: d.id,
          color: colorForIndex(i),
          ...d.data(),
        }));
        setFeeds(rows);
      },
      (err) => {
        console.error(err);
        setMsg("Couldn't load external calendars.");
      }
    );
    return () => unsub();
  }, [db, selectedListingId]);

  /** ---------------- Build maps for UI (base & overlays) -------------- **/
  const { grid, first, last } = useMemo(
    () => buildMonthGrid(monthAnchor),
    [monthAnchor]
  );

  /** ------------ Live listener for imported external busy days -------- **/
  useEffect(() => {
    if (!selectedListingId) {
      setCalendarBlocks([]);
      return;
    }
    const calRef = collection(db, "listings", selectedListingId, "calendar");
    const from = ymd(grid[0]);
    const to = ymd(grid[grid.length - 1]);

    const qCal = query(
      calRef,
      orderBy(documentId()),
      startAt(from),
      endAt(to)
    );

    const unsub = onSnapshot(
      qCal,
      (snap) => {
        const rows = snap.docs.map((d) => ({
          id: d.id,
          date: d.id, // docId is YYYY-MM-DD
          ...(d.data() || {}),
        }));
        setCalendarBlocks(rows);
      },
      (err) => {
        console.error(err);
      }
    );

    return () => unsub();
  }, [db, selectedListingId, grid]);

  // Base map from DB bookings
  const baseMap = useMemo(() => {
    const from = ymd(first);
    const to = ymd(last);
    const map = new Map();
    for (const b of bookings) {
      const d = String(b.date || "");
      if (!d || d < from || d > to) continue;
      map.set(d, {
        status: b.status || "pending",
        note: b.note || "",
        guestUid: b.guestUid || "",
        isActive: b.isActive === true,
        hostUid: b.hostUid || "",
        groupId: b.groupId || null,
        type: b.type || (b.status === "blocked" ? "block" : "booking"),
      });
    }
    return map;
  }, [bookings, first, last]);

  // Rule overlays
  const overlayMap = useMemo(() => {
    const map = new Map();
    if (!selectedListingRules) return map;

    const today = startOfToday();
    const windowEnd = addDays(
      today,
      Number(selectedListingRules.bookingWindowDays || 0)
    );

    for (const d of grid) {
      const ds = ymd(d);
      if (startOfDay(d) > windowEnd)
        map.set(ds, { status: "hold", holdType: "window" });
    }

    const allowed = new Set(selectedListingRules.allowedWeekdays || []);
    for (const d of grid) {
      const ds = ymd(d);
      if (!allowed.has(d.getDay()) && !map.has(ds))
        map.set(ds, { status: "hold", holdType: "weekday" });
    }

    const cutoffH = Number(selectedListingRules.advanceNoticeHours || 0);
    if (cutoffH > 0) {
      const now = new Date();
      const cutoff = new Date();
      cutoff.setHours(now.getHours() + cutoffH, now.getMinutes(), 0, 0);
      const dsToday = ymd(today);
      if (now < cutoff && !map.has(dsToday))
        map.set(dsToday, { status: "hold", holdType: "cutoff" });
    }

    const before = Number(selectedListingRules.prepDaysBefore || 0);
    const after = Number(selectedListingRules.prepDaysAfter || 0);

    if (before > 0 || after > 0) {
      const seen = new Set();
      for (const [ds, info] of baseMap.entries()) {
        if (seen.has(ds)) continue;
        if (info?.isActive && info.status === "paid") {
          const span = collectGroupDatesFromMap(baseMap, ds);
          span.forEach((x) => seen.add(x));

          if (before > 0) {
            const s = parseYYYYMMDD(span[0]);
            for (let i = 1; i <= before; i++) {
              const dd = ymd(addDays(s, -i));
              if (
                dd >= ymd(first) &&
                dd <= ymd(last) &&
                !baseMap.has(dd) &&
                !map.has(dd)
              )
                map.set(dd, { 
  status: "hold", 
  holdType: "prep",
  isBuffer: true // ← ADD THIS LINE
});
            }
          }

          if (after > 0) {
            const e = parseYYYYMMDD(span[span.length - 1]);
            for (let i = 1; i <= after; i++) {
              const dd = ymd(addDays(e, +i));
              if (
                dd >= ymd(first) &&
                dd <= ymd(last) &&
                !baseMap.has(dd) &&
                !map.has(dd)
              )
                map.set(dd, { 
  status: "hold", 
  holdType: "prep",
  isBuffer: true // ← ADD THIS LINE
});
            }
          }
        }
      }
    }

    return map;
  }, [grid, first, last, baseMap, selectedListingRules]);

  // External previews overlay (front-end preview only)
  const externalOverlayMap = useMemo(() => {
    const map = new Map();
    const from = ymd(first);
    const to = ymd(last);

    for (const f of feeds) {
      const events = previewEvents[f.id] || [];
      for (const ev of events) {
        for (const ds of ev.days) {
          if (ds < from || ds > to) continue;
          if (!baseMap.has(ds)) {
            if (!map.has(ds)) {
              map.set(ds, {
                status: "external",
                source: f.source || detectSource(f.url || ""),
                label: f.label || "External",
                color: f.color || BRAND_DARK,
                feedId: f.id,
              });
            } else {
              const cur = map.get(ds);
              cur.multi = true;
              map.set(ds, cur);
            }
          }
        }
      }
    }

    return map;
  }, [feeds, previewEvents, baseMap, first, last]);

  // Imported external busy map from Firestore (live sync)
  const calendarMap = useMemo(() => {
    const map = new Map();
    const from = ymd(first);
    const to = ymd(last);

    for (const row of calendarBlocks) {
      const ds = String(row.date || row.id || "");
      if (!ds || ds < from || ds > to) continue;

      const status = row.status || "external";
      const active = row.isActive !== false;

      if (!active) continue;
      if (status === "free" || status === "open" || status === "canceled")
        continue;

      map.set(ds, {
        status: "external",
        source: row.source || detectSource(row.url || ""),
        label: row.label || row.source || "External",
        color: row.color || BRAND_DARK,
        feedId: row.feedId || null,
        isActive: active,
      });
    }

    return map;
  }, [calendarBlocks, first, last]);

  // Final map priority: base > rule holds > live external busy > preview external
  const monthMap = useMemo(() => {
    const map = new Map(baseMap);
    for (const [ds, hold] of overlayMap.entries())
      if (!map.has(ds)) map.set(ds, { ...hold, isActive: false });
    for (const [ds, extLive] of calendarMap.entries())
      if (!map.has(ds)) map.set(ds, { ...extLive, isActive: false });
    for (const [ds, ext] of externalOverlayMap.entries())
      if (!map.has(ds)) map.set(ds, { ...ext, isActive: false });
    return map;
  }, [baseMap, overlayMap, calendarMap, externalOverlayMap]);

  const selectedDateStr = selectedDate ? ymd(selectedDate) : "";

  /** ---------------- Span role for ribbons on booked/blocked ---------- **/
  const spanRoleFor = useCallback(
    (dateStr) => {
      const info = baseMap.get(dateStr);
      if (!info?.isActive || !info.groupId) return null;

      const d = parseYYYYMMDD(dateStr);
      if (!d) return null;

      const prevStr = ymd(addDays(d, -1));
      const nextStr = ymd(addDays(d, +1));
      const prev = baseMap.get(prevStr);
      const next = baseMap.get(nextStr);

      const samePrev = prev?.isActive && prev.groupId === info.groupId;
      const sameNext = next?.isActive && next.groupId === info.groupId;

      if (samePrev && sameNext) return "mid";
      if (!samePrev && sameNext) return "start";
      if (samePrev && !sameNext) return "end";
      return "single";
    },
    [baseMap]
  );

  const collectGroupDates = useCallback(
    (dateStr) => collectGroupDatesFromMap(baseMap, dateStr),
    [baseMap]
  );

  /** ========================== Mobile gestures ======================== **/
  const lpTimer = useRef(null);
  const lpFired = useRef(false);
  const LONG_PRESS_MS = 500;

  const clearLP = () => {
    if (lpTimer.current) {
      clearTimeout(lpTimer.current);
      lpTimer.current = null;
    }
  };

  const onCellPointerDown = (dateObj) => {
    clearLP();
    lpFired.current = false;
    lpTimer.current = setTimeout(async () => {
      lpFired.current = true;
      try {
        const ds = ymd(dateObj);
        const base = baseMap.get(ds);
        const overlay = overlayMap.get(ds) || monthMap.get(ds);

        if (base?.isActive && base.status === "blocked") {
          await handleUnblock(dateObj);
        } else if (
          (!base || !base.isActive) &&
          startOfDay(dateObj) >= startOfToday() &&
          overlay?.status !== "hold"
        ) {
          await handleBlock(dateObj);
        }
        if (navigator?.vibrate) navigator.vibrate(12);
      } finally {
        clearLP();
      }
    }, LONG_PRESS_MS);
  };

  const onCellPointerUp = () => {
    clearLP();
    setTimeout(() => {
      lpFired.current = false;
    }, 0);
  };

  const swipeRef = useRef({ x: 0, y: 0 });
  const onTouchStart = (e) => {
    const t = e.touches?.[0];
    if (!t) return;
    swipeRef.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e) => {
    const t = e.changedTouches?.[0];
    if (!t) return;
    const dx = t.clientX - swipeRef.current.x;
    const dy = t.clientY - swipeRef.current.y;
    if (Math.abs(dx) > 48 && Math.abs(dy) < 40) {
      setMonthAnchor(addMonths(monthAnchor, dx < 0 ? +1 : -1));
      setSelectedDate(null);
    }
  };

  /** ============================= Actions ============================= **/
  async function handleBlock(dateObj) {
    if (!user || !selectedListingId) return;
    const ds = ymd(dateObj);
    const existing = baseMap.get(ds);

    if (existing?.isActive) {
      setMsg("That date is already unavailable.");
      return;
    }
    if (startOfDay(dateObj) < startOfToday()) {
      setMsg("You can’t block past dates.");
      return;
    }

    setSaving(true);
    setMsg("");
    try {
      await createBlock({
        listingId: selectedListingId,
        hostUid: user.uid,
        date: ds,
        note: note || "Host blocked",
        type: "block",
      });
      setMsg("Date blocked.");
    } catch (e) {
      console.error(e);
      setMsg(e?.message || "Couldn't block that date.");
    } finally {
      setSaving(false);
    }
  }

  async function handleUnblock(dateObj) {
    if (!user || !selectedListingId) return;
    const ds = ymd(dateObj);
    const existing = baseMap.get(ds);

    if (!existing || existing.status !== "blocked" || !existing.isActive) {
      setMsg("That date isn’t a host block.");
      return;
    }

    setSaving(true);
    setMsg("");
    try {
      await unblockDate({ listingId: selectedListingId, date: ds });
      setMsg("Date unblocked.");
    } catch (e) {
      console.error(e);
      setMsg(e?.message || "Couldn't unblock that date.");
    } finally {
      setSaving(false);
    }
  }

  async function handleBlockRange() {
    if (!user || !selectedListingId) return;
    const s = parseYYYYMMDD(rangeStart);
    const e = parseYYYYMMDD(rangeEnd);

    if (!s || !e) {
      setMsg("Choose both a start and end date.");
      return;
    }
    if (e < s) {
      setMsg("End date must be after start date.");
      return;
    }
    if (startOfDay(s) < startOfToday()) {
      setMsg("You can’t start a block in the past.");
      return;
    }

    const days = [];
    let d = new Date(s);
    while (d <= e) {
      const ds = ymd(d);
      const ex = baseMap.get(ds);
      if (ex?.isActive) {
        setMsg(`Cannot block: ${ds} is already unavailable.`);
        return;
      }
      days.push(ds);
      d = addDays(d, 1);
    }

    setSaving(true);
    setMsg("");
    try {
      const groupId = randGroupId();
      for (const ds of days) {
        // eslint-disable-next-line no-await-in-loop
        await createBlock({
          listingId: selectedListingId,
          hostUid: user.uid,
          date: ds,
          note: note || "Host blocked",
          type: "block",
          groupId,
        });
      }
      setMsg(
        `Blocked ${days.length} day${days.length === 1 ? "" : "s"} (${rangeStart} → ${rangeEnd}).`
      );
      setRangeStart("");
      setRangeEnd("");
      setRangeMode(false);
    } catch (e) {
      console.error(e);
      setMsg(e?.message || "Couldn't create that range block.");
    } finally {
      setSaving(false);
    }
  }

  async function handleUnblockSpan(dateObj) {
    if (!user || !selectedListingId) return;
    const ds = ymd(dateObj);
    const info = baseMap.get(ds);

    if (!info?.isActive || info.status !== "blocked") {
      setMsg("That date isn’t a host block.");
      return;
    }

    const dates = collectGroupDates(ds);
    if (!dates?.length) {
      setMsg("No span found to unblock.");
      return;
    }

    setSaving(true);
    setMsg("");
    try {
      for (const d of dates) {
        // eslint-disable-next-line no-await-in-loop
        await unblockDate({ listingId: selectedListingId, date: d });
      }
      setMsg(
        `Unblocked ${dates.length} day${dates.length === 1 ? "" : "s"} in the span.`
      );
    } catch (e) {
      console.error(e);
      setMsg(e?.message || "Couldn't unblock that span.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveRules() {
    if (!selectedListingId || !selectedListingRules) return;
    setSaving(true);
    setMsg("");
    try {
      await updateDoc(doc(db, "listings", selectedListingId), {
  bookingRules: {
    ...(selectedListingRules || DEFAULT_RULES),
    prepDaysBefore: clamp(
      Number(selectedListingRules?.prepDaysBefore) || 0,
      0,
      3
    ),
    prepDaysAfter: clamp(
      Number(selectedListingRules?.prepDaysAfter) || 0,
      0,
      3
    ),
    bufferDaysBefore: clamp( // ← ADD THIS
      Number(selectedListingRules?.prepDaysBefore) || 0,
      0,
      3
    ),
    bufferDaysAfter: clamp( // ← ADD THIS
      Number(selectedListingRules?.prepDaysAfter) || 0,
      0,
      3
    ),
  },

  // ✅ mirror for legacy/other screens
  minNights: selectedListingRules?.minNights ?? null,
  maxNights: selectedListingRules?.maxNights ?? null,

  // ✅ mirror buffer days at top level
  prepDaysBefore: clamp(
    Number(selectedListingRules?.prepDaysBefore) || 0,
    0,
    3
  ),
  prepDaysAfter: clamp(
    Number(selectedListingRules?.prepDaysAfter) || 0,
    0,
    3
  ),
  // ✅ Add these top-level buffer fields too:
  bufferDaysBefore: clamp(
    Number(selectedListingRules?.prepDaysBefore) || 0,
    0,
    3
  ),
  bufferDaysAfter: clamp(
    Number(selectedListingRules?.prepDaysAfter) || 0,
    0,
    3
  ),
  // ✅ Also consider adding turnoverDays for backward compatibility:
  turnoverDays: clamp(
    Math.max(
      Number(selectedListingRules?.prepDaysBefore) || 0,
      Number(selectedListingRules?.prepDaysAfter) || 0
    ),
    0,
    3
  ),
});


      setRulesDirty(false);
      setMsg("Availability rules saved.");
    } catch (e) {
      console.error(e);
      setMsg(e?.message || "Couldn't save rules.");
    } finally {
      setSaving(false);
    }
  }

  /** --------------------- External Feeds (Sync Center) ---------------- **/

  // ✅ PERSISTENCE: call server importNow to persist full calendar
  const importNowForFeed = useCallback(
    async (feed) => {
      if (!user || !selectedListingId || !feed?.id) return null;
      const token = await user.getIdToken();
      const res = await fetch(`${ICS_PROXY_BASE}/importNow`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          listingId: selectedListingId,
          feedId: feed.id,
        }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || "Import failed.");
      }
      return res.json();
    },
    [user, selectedListingId]
  );

  async function addFeed() {
    if (!selectedListingId) return;
    const url = (newFeed.url || "").trim();
    if (!/^https?:\/\//i.test(url)) {
      setMsg("Enter a valid https:// iCal URL.");
      return;
    }

    const source = detectSource(url);
    const label = (newFeed.label || "").trim() || source.toUpperCase();

    setAddingFeed(true);
    setMsg("");
    try {
      const ref = collection(
        db,
        "listings",
        selectedListingId,
        "calendarFeeds"
      );
      const docRef = await addDoc(ref, {
        label,
        url,
        status: "pending",
        source,
        autoImport: !!newFeed.autoImport,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastSync: null,
      });

      setNewFeed({ label: "", url: "", autoImport: true });

      // ✅ PERSISTENCE: Import full horizon immediately
      setMsg("Calendar added. Importing full availability…");
      try {
        const r = await importNowForFeed({
          id: docRef.id,
        });
        if (r?.imported != null) {
          setMsg(`Imported ${r.imported} busy day(s). Fetching preview…`);
        } else {
          setMsg("Import complete. Fetching preview…");
        }
      } catch (e) {
        console.error(e);
        setMsg(
          "Calendar added, but full import failed. Previewing visible dates only…"
        );
      }

      // AUTO-PREVIEW for immediate overlays
      await testAndPreview({
        id: docRef.id,
        label,
        url,
        autoImport: !!newFeed.autoImport,
        source,
        color: BRAND_DARK,
      });
    } catch (e) {
      console.error(e);
      if (String(e?.message || "").toLowerCase().includes("permission")) {
        setMsg(
          "Permissions error adding calendar. Update Firestore rules for listings/{id}/calendarFeeds."
        );
      } else {
        setMsg(e?.message || "Couldn't add that calendar.");
      }
    } finally {
      setAddingFeed(false);
    }
  }

  async function removeFeed(feedId) {
    if (!selectedListingId || !feedId) return;
    try {
      await deleteDoc(
        doc(db, "listings", selectedListingId, "calendarFeeds", feedId)
      );
      setMsg("Calendar removed.");
      setPreviewEvents((p) => {
        const x = { ...p };
        delete x[feedId];
        return x;
      });
    } catch (e) {
      console.error(e);
      setMsg("Couldn't remove the calendar.");
    }
  }

  async function toggleAutoImport(feed) {
    try {
      await updateDoc(
        doc(db, "listings", selectedListingId, "calendarFeeds", feed.id),
        {
          autoImport: !feed.autoImport,
          updatedAt: serverTimestamp(),
        }
      );
    } catch (e) {
      console.error(e);
      setMsg("Failed to update auto-import.");
    }
  }

  // ✅ useCallback to stabilize identity for auto-preview effect
  const testAndPreview = useCallback(
    async (feed) => {
      if (!selectedListingId) return;
      setTestingFeedId(feed.id);
      try {
        const text = await fetchICS(feed.url);
        const all = parseICS(text);

        const windowStart = addDays(grid[0], -7);
        const windowEnd = addDays(grid[grid.length - 1], 7);
        const filtered = all.filter(
          (ev) => ev.end >= windowStart && ev.start <= windowEnd
        );

        setPreviewEvents((prev) => ({ ...prev, [feed.id]: filtered }));
        await updateDoc(
          doc(db, "listings", selectedListingId, "calendarFeeds", feed.id),
          { status: "ok", updatedAt: serverTimestamp() }
        );

        setMsg(
          filtered.length
            ? `${filtered.length} busy event${filtered.length === 1 ? "" : "s"} found.`
            : "No busy events near visible dates."
        );
      } catch (e) {
        console.error(e);
        setMsg(
          "Couldn’t load that iCal feed. Make sure your /api/ics/proxy is deployed."
        );
        try {
          await updateDoc(
            doc(db, "listings", selectedListingId, "calendarFeeds", feed.id),
            { status: "error", updatedAt: serverTimestamp() }
          );
        } catch (_) {}
      } finally {
        setTestingFeedId("");
      }
    },
    [db, grid, selectedListingId]
  );

  // ✅ Clear preview+guards when switching listings
  useEffect(() => {
    didPreviewRef.current = new Set();
    didImportRef.current = new Set();
    setPreviewEvents({});
  }, [selectedListingId]);

  // ✅ Auto-preview existing feeds once on landing
  useEffect(() => {
    if (!selectedListingId || feeds.length === 0) return;
    (async () => {
      for (const f of feeds) {
        if (!f?.id || !f?.url) continue;
        if (didPreviewRef.current.has(f.id)) continue;
        didPreviewRef.current.add(f.id);
        await testAndPreview(f);
      }
    })();
  }, [feeds, selectedListingId, testAndPreview]);

  // ✅ PERSISTENCE: One-time auto-import for feeds that haven't synced yet
  useEffect(() => {
    if (!selectedListingId || feeds.length === 0 || !user) return;
    (async () => {
      for (const f of feeds) {
        if (!f?.id || !f?.url) continue;
        if (!f.autoImport) continue;
        if (didImportRef.current.has(f.id)) continue;

        const needsImport = !f.lastSync || f.status === "pending";
        if (!needsImport) continue;

        didImportRef.current.add(f.id);
        try {
          setMsg(`Importing ${f.label || "calendar"}…`);
          await importNowForFeed(f);
          setMsg(`Imported ${f.label || "calendar"}.`);
        } catch (e) {
          console.error(e);
          // don't hard fail UI for auto-import
          setMsg(`Auto-import failed for ${f.label || "calendar"}.`);
        }
      }
    })();
  }, [feeds, selectedListingId, user, importNowForFeed]);

  async function refreshAllFeeds() {
    if (!feeds.length) {
      setMsg("No external calendars to refresh.");
      return;
    }
    setSaving(true);
    setMsg("Refreshing external calendars…");
    try {
      for (const f of feeds) {
        // ✅ PERSISTENCE: refresh persists full horizon
        try {
          await importNowForFeed(f);
        } catch (e) {
          console.error(e);
        }
        // preview remains for UI overlay hints
        // eslint-disable-next-line no-await-in-loop
        await testAndPreview(f);
      }
      setMsg("External calendars refreshed.");
    } catch (e) {
      console.error(e);
      setMsg("Some calendars failed to refresh.");
    } finally {
      setSaving(false);
    }
  }

  async function applyExternalAsBlocks() {
    if (!user || !selectedListingId) return;
    const candidates = [];
    for (const d of grid) {
      const ds = ymd(d);
      const info = monthMap.get(ds);
      if (info && info.status === "external") candidates.push(ds);
    }
    if (!candidates.length) {
      setMsg("No external busy days in view to apply.");
      return;
    }

    setSaving(true);
    setMsg("");
    try {
      let applied = 0;
      for (const ds of candidates) {
        if (!baseMap.get(ds)) {
          // eslint-disable-next-line no-await-in-loop
          await createBlock({
            listingId: selectedListingId,
            hostUid: user.uid,
            date: ds,
            note: "External calendar busy",
            type: "block",
          });
          applied++;
        }
      }
      setMsg(
        `Applied ${applied} busy day${applied === 1 ? "" : "s"} as host blocks.`
      );
    } catch (e) {
      console.error(e);
      setMsg("Failed to apply some days.");
    } finally {
      setSaving(false);
    }
  }

  /** ------------------------------- Copy ------------------------------ **/
  async function copyText(text, which) {
    try {
      await navigator.clipboard.writeText(text);
      if (which === "https") {
        setCopiedHttps(true);
        setTimeout(() => setCopiedHttps(false), 1200);
      } else {
        setCopiedWebcal(true);
        setTimeout(() => setCopiedWebcal(false), 1200);
      }
    } catch (e) {
      console.error(e);
      setMsg("Couldn't copy to clipboard.");
    }
  }

  /** --------------------------- Export snapshot ------------------------ **/
  function exportVisibleMonthICS() {
    const events = [];
    const seen = new Set();
    const toICSDate = (s) => s.replace(/-/g, "");
    const pushEvent = (summary, startStr, endStr) => {
      const uid = `${summary}-${startStr}-${endStr}-${selectedListingId}@yourewelcome`;
      events.push(
        [
          "BEGIN:VEVENT",
          `UID:${uid}`,
          `DTSTAMP:${toICSDate(ymd(new Date()))}T000000Z`,
          `DTSTART;VALUE=DATE:${toICSDate(startStr)}`,
          `DTEND;VALUE=DATE:${toICSDate(endStr)}`,
          `SUMMARY:${summary}`,
          "END:VEVENT",
        ].join("\r\n")
      );
    };

    const buildSpans = (wantStatus) => {
      const spans = [];
      for (const [ds, info] of baseMap.entries()) {
        if (seen.has(ds)) continue;
        if (!(info?.isActive) || info.status !== wantStatus) continue;
        const group = collectGroupDatesFromMap(baseMap, ds);
        group.forEach((x) => seen.add(x));
        spans.push(group);
      }
      return spans;
    };

    for (const span of buildSpans("paid")) {
      const start = span[0];
      const endExclusive = ymd(
        addDays(parseYYYYMMDD(span[span.length - 1]), 1)
      );
      pushEvent("Booked", start, endExclusive);
    }
    for (const span of buildSpans("blocked")) {
      const start = span[0];
      const endExclusive = ymd(
        addDays(parseYYYYMMDD(span[span.length - 1]), 1)
      );
      pushEvent("Blocked", start, endExclusive);
    }

    const ics = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//YoureWelcome//HostCalendar//EN",
      ...events,
      "END:VCALENDAR",
    ].join("\r\n");

    const blob = new Blob([ics], {
      type: "text/calendar;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const label = monthAnchor
      .toLocaleString(undefined, { month: "long", year: "numeric" })
      .replace(/\s+/g, "_");
    a.download = `calendar_${label}.ics`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  /** ============================= Render ============================== **/
  if (loading) {
    return (
      <div>
        <h1 className="text-2xl font-semibold">Calendar</h1>
        <p className="mt-2 text-neutral-600">
          View availability, rules, and sync calendars.
        </p>
        <div className="mt-6 rounded-2xl border border-neutral-200 p-6">
          <div className="h-40 animate-pulse rounded-xl bg-neutral-200" />
        </div>
      </div>
    );
  }
  if (!user) return null;

  const feedUrlHttps = selectedListingId
    ? `${ICS_FEED_BASE}/${selectedListingId}.ics`
    : "";
  const feedUrlWebcal = selectedListingId
    ? `webcal://${APP_DOMAIN}/ical/${selectedListingId}.ics`
    : "";

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-semibold">Calendar</h1>
          <p className="mt-1 text-sm text-neutral-600">
            Manage availability and sync external calendars.
          </p>
        </div>

        {/* Mobile: open controls */}
        <div className="flex items-center gap-2">
          {showInstallCTA && (
            <button
              type="button"
              onClick={handleInstallClick}
              className="inline-flex items-center rounded-full border border-neutral-300 px-3 py-1.5 text-sm hover:bg-neutral-50 lg:hidden"
              title="Install app"
            >
              Install app
            </button>
          )}
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="ml-1 inline-flex items-center rounded-full border border-neutral-300 px-3 py-1.5 text-sm hover:bg-neutral-50 lg:hidden"
          >
            Settings
          </button>
          {listings.length === 0 && (
            <Link
              to={ROUTES.newListing}
              className="hidden rounded-full px-4 py-2 text-sm font-semibold text-white lg:inline-block"
              style={{ backgroundColor: BRAND }}
            >
              Add your first listing
            </Link>
          )}
        </div>
      </div>

      {!!msg && (
        <div
          className="mt-4 rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-neutral-800"
          aria-live="polite"
        >
          {msg}
        </div>
      )}

      {/* Listing picker */}
      {listings.length > 0 && (
        <div className="mt-4">
          <label className="text-sm">Listing</label>
          <select
            value={selectedListingId}
            onChange={(e) => {
              setSelectedListingId(e.target.value);
              setSelectedDate(null);
              setMsg("");
            }}
            className="mt-1 w-full max-w-md rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
          >
            {listings.map((l) => (
              <option key={l.id} value={l.id}>
                {l.title}
                {l.city || l.stateCode
                  ? ` · ${[l.city, l.stateCode].filter(Boolean).join(", ")}`
                  : ""}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Calendar + Controls */}
      <div className="mt-4 grid gap-6 lg:grid-cols-[1fr,380px]">
        {/* Calendar card */}
        <div
          className="rounded-2xl border border-neutral-200 p-3 sm:p-4"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {/* Month header */}
          <div className="sticky top-0 z-[1] -m-3 -mb-2 rounded-t-2xl bg-white/95 p-3 backdrop-blur">
            <div className="flex items-center justify-between gap-2">
              <div className="text-lg font-semibold">
                {monthAnchor.toLocaleString(undefined, {
                  month: "long",
                  year: "numeric",
                })}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setMonthAnchor(addMonths(monthAnchor, -1));
                    setSelectedDate(null);
                  }}
                  className="rounded-full border border-neutral-300 px-3 py-1.5 text-sm hover:bg-neutral-50"
                  aria-label="Previous month"
                >
                  ←
                </button>
                <button
                  onClick={() => {
                    const now = new Date();
                    setMonthAnchor(startOfMonth(now));
                    setSelectedDate(now);
                  }}
                  className="rounded-full border border-neutral-300 px-3 py-1.5 text-sm hover:bg-neutral-50"
                  aria-label="Jump to today"
                >
                  Today
                </button>
                <button
                  onClick={() => {
                    setMonthAnchor(addMonths(monthAnchor, +1));
                    setSelectedDate(null);
                  }}
                  className="rounded-full border border-neutral-300 px-3 py-1.5 text-sm hover:bg-neutral-50"
                  aria-label="Next month"
                >
                  →
                </button>
              </div>
            </div>
          </div>

          {/* Weekday header */}
          <div className="mt-2 grid grid-cols-7 gap-1 text-center text-xs text-neutral-500">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <div key={d} className="py-1">
                {d}
              </div>
            ))}
          </div>

          {/* Grid */}
          <div className="mt-1 grid grid-cols-7 gap-1">
            {grid.map((d) => {
              const inMonth =
                d.getMonth() === monthAnchor.getMonth() &&
                d.getFullYear() === monthAnchor.getFullYear();
              const isToday = isSameDay(d, new Date());
              const isSelected = selectedDate && isSameDay(d, selectedDate);
              const ds = ymd(d);
              const info = monthMap.get(ds);
              const base = baseMap.get(ds);
              const role = spanRoleFor(ds);

              let badge = null;
              let subt = null;
              let style = null;

              if (base?.isActive) {
                if (base.status === "pending")
                  badge = {
                    label: "Pending",
                    cls: "bg-amber-100 text-amber-700 border-amber-200",
                  };
                else if (base.status === "blocked")
                  badge = {
                    label: "Blocked",
                    cls: "bg-neutral-200 text-neutral-700 border-neutral-300",
                  };
                else if (base.status === "paid")
                  badge = {
                    label: "Booked",
                    cls: "bg-red-100 text-red-700 border-red-200",
                  };
                else
                  badge = {
                    label: "Unavailable",
                    cls: "bg-neutral-100 text-neutral-700 border-neutral-200",
                  };
              } else if (info && info.status === "hold") {
                badge = {
                  label: "Closed",
                  cls: "bg-neutral-100 text-neutral-600 border-neutral-200",
                };
              } else if (info && info.status === "external") {
                const grad = `linear-gradient(135deg, ${
                  info.color || BRAND_DARK
                }22, ${(info.color || BRAND_DARK) + "44"})`;
                badge = {
                  label: "Busy",
                  cls: "border",
                  style: {
                    background: grad,
                    borderColor: (info.color || BRAND_DARK) + "66",
                    color: "#1f2937",
                  },
                };
                style = badge.style;
                subt = info.multi ? "Multiple" : null;
              }

              const canBlock =
                inMonth &&
                (!base || !base.isActive) &&
                startOfDay(d) >= startOfToday();

              const spanBar =
                role && (base?.status === "blocked" || base?.status === "paid") ? (
                  <div
                    className={[
                      "absolute bottom-1 left-1 right-1 h-1.5",
                      base.status === "paid" ? "bg-red-300" : "bg-neutral-400",
                      role === "start" ? "rounded-l-full" : "",
                      role === "end" ? "rounded-r-full" : "",
                      role === "single" ? "rounded-full" : "",
                    ].join(" ")}
                    aria-hidden
                  />
                ) : null;

              return (
                <button
                  key={ds}
                  onClick={() => {
                    if (lpFired.current) return;
                    setSelectedDate(d);
                  }}
                  onPointerDown={() => onCellPointerDown(d)}
                  onPointerUp={onCellPointerUp}
                  onPointerLeave={onCellPointerUp}
                  className={[
                    "relative h-24 sm:h-20 rounded-xl border text-left p-2 transition",
                    inMonth
                      ? "bg-white border-neutral-200"
                      : "bg-neutral-50 border-neutral-200/70 text-neutral-400",
                    isSelected ? "ring-2 ring-black" : "hover:shadow-sm",
                  ].join(" ")}
                >
                  <div className="flex items-center justify-between">
                    <span className={`text-xs ${isToday ? "font-semibold" : ""}`}>
                      {d.getDate()}
                    </span>
                    {badge && (
                      <span
                        className={`rounded-full border px-1.5 py-0.5 text-[10px] ${
                          badge.cls || ""
                        }`}
                        style={style || undefined}
                      >
                        {badge.label}
                      </span>
                    )}
                  </div>
                  {subt && (
                    <div className="mt-1 text-[10px] text-neutral-500">
                      {subt}
                    </div>
                  )}
                  {!badge && canBlock && (
                    <div className="absolute bottom-2 left-2 text-[10px] text-neutral-500">
                      Available
                    </div>
                  )}
                  {spanBar}
                </button>
              );
            })}
          </div>

          {/* Minimal legend */}
          <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-neutral-500">
            <span className="inline-flex items-center gap-1">
              <span className="h-2.5 w-2.5 rounded-full bg-red-200" /> Booked
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="h-2.5 w-2.5 rounded-full bg-amber-200" /> Pending
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="h-2.5 w-2.5 rounded-full bg-neutral-300" /> Blocked
            </span>
            <span className="inline-flex items-center gap-1">
              <span
                className="h-2.5 w-2.5 rounded border"
                style={{
                  background: `linear-gradient(135deg, ${BRAND_DARK}22, ${BRAND_DARK}44)`,
                }}
              />{" "}
              External busy
            </span>
          </div>
        </div>

        {/* Right rail (desktop) */}
        <aside className="hidden lg:block">
          <RailContent
            selectedDate={selectedDate}
            selectedDateStr={selectedDateStr}
            baseMap={baseMap}
            overlayMap={overlayMap}
            monthMap={monthMap}
            grid={grid}
            monthAnchor={monthAnchor}
            selectedListingId={selectedListingId}
            selectedListingRules={selectedListingRules}
            DEFAULT_RULES={DEFAULT_RULES}
            rulesDirty={rulesDirty}
            setSelectedListingRules={setSelectedListingRules}
            setRulesDirty={setRulesDirty}
            rangeMode={rangeMode}
            setRangeMode={setRangeMode}
            rangeStart={rangeStart}
            setRangeStart={setRangeStart}
            rangeEnd={rangeEnd}
            setRangeEnd={setRangeEnd}
            note={note}
            setNote={setNote}
            saving={saving}
            handleBlock={handleBlock}
            handleUnblock={handleUnblock}
            handleUnblockSpan={handleUnblockSpan}
            handleBlockRange={handleBlockRange}
            handleSaveRules={handleSaveRules}
            collectGroupDates={collectGroupDates}
            feeds={feeds}
            previewEvents={previewEvents}
            newFeed={newFeed}
            setNewFeed={setNewFeed}
            addingFeed={addingFeed}
            addFeed={addFeed}
            refreshAllFeeds={refreshAllFeeds}
            testAndPreview={testAndPreview}
            toggleAutoImport={toggleAutoImport}
            removeFeed={removeFeed}
            applyExternalAsBlocks={applyExternalAsBlocks}
            testingFeedId={testingFeedId}
            feedUrlHttps={feedUrlHttps}
            feedUrlWebcal={feedUrlWebcal}
            copyText={copyText}
            copiedHttps={copiedHttps}
            copiedWebcal={copiedWebcal}
            exportVisibleMonthICS={exportVisibleMonthICS}
          />
        </aside>
      </div>

      {/* -------------------- Mobile Bottom Sheet -------------------- */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div
            className="absolute inset-x-0 bottom-0 max-h-[85vh] rounded-t-2xl border border-neutral-200 bg-white"
            style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
          >
            <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
              <div className="text-sm font-medium">Settings</div>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="rounded-md border border-neutral-300 px-2 py-1 text-xs"
              >
                Close
              </button>
            </div>
            <div className="max-h-[75vh] overflow-y-auto px-4 pb-4 pt-3">
              <RailContent
                compact
                selectedDate={selectedDate}
                selectedDateStr={selectedDateStr}
                baseMap={baseMap}
                overlayMap={overlayMap}
                monthMap={monthMap}
                grid={grid}
                monthAnchor={monthAnchor}
                selectedListingId={selectedListingId}
                selectedListingRules={selectedListingRules}
                DEFAULT_RULES={DEFAULT_RULES}
                rulesDirty={rulesDirty}
                setSelectedListingRules={setSelectedListingRules}
                setRulesDirty={setRulesDirty}
                rangeMode={rangeMode}
                setRangeMode={setRangeMode}
                rangeStart={rangeStart}
                setRangeStart={setRangeStart}
                rangeEnd={rangeEnd}
                setRangeEnd={setRangeEnd}
                note={note}
                setNote={setNote}
                saving={saving}
                handleBlock={handleBlock}
                handleUnblock={handleUnblock}
                handleUnblockSpan={handleUnblockSpan}
                handleBlockRange={handleBlockRange}
                handleSaveRules={handleSaveRules}
                collectGroupDates={collectGroupDates}
                feeds={feeds}
                previewEvents={previewEvents}
                newFeed={newFeed}
                setNewFeed={setNewFeed}
                addingFeed={addingFeed}
                addFeed={addFeed}
                refreshAllFeeds={refreshAllFeeds}
                testAndPreview={testAndPreview}
                toggleAutoImport={toggleAutoImport}
                removeFeed={removeFeed}
                applyExternalAsBlocks={applyExternalAsBlocks}
                testingFeedId={testingFeedId}
                feedUrlHttps={feedUrlHttps}
                feedUrlWebcal={feedUrlWebcal}
                copyText={copyText}
                copiedHttps={copiedHttps}
                copiedWebcal={copiedWebcal}
                exportVisibleMonthICS={exportVisibleMonthICS}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
